import { useState } from "react";
import { useStripe, useElements, PaymentElement, LinkAuthenticationElement } from "@stripe/react-stripe-js";

interface CheckoutFormProps {
  onSuccess: () => void;
}

const CheckoutForm = ({ onSuccess }: CheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    setMessage("");

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/setup-complete`,
        payment_method_data: {
          billing_details: {
            email: email,
            name: name,
          }
        }
      },
      redirect: "if_required",
    });

    if (error) {
      setMessage(error.message || "An error occurred during setup");
    } else if (setupIntent) {
      switch (setupIntent.status) {
        case "succeeded":
          setMessage("Payment method saved successfully!");
          setTimeout(() => {
            onSuccess();
          }, 1000);
          break;
        case "processing":
          setMessage("Your payment is being processed...");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        case "requires_confirmation":
          setMessage("Payment requires additional confirmation.");
          break;
        case "requires_action":
          setMessage("Additional action required to complete setup.");
          break;
        case "canceled":
          setMessage("Payment setup was canceled.");
          break;
        default:
          setMessage("Payment setup completed with an unexpected status. Please contact support if you experience issues.");
          break;
      }
    } else {
      setMessage("An unexpected error occurred. Please try again or contact support.");
    }

    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Payment & Contact Info</h3>
        
        <div className="space-y-4">
          <div className="border border-gray-300 rounded-lg p-3">
            <LinkAuthenticationElement
              onChange={(event) => {
                if (event.value.email) {
                  setEmail(event.value.email);
                }
              }}
            />
          </div>

          <div className="border border-gray-300 rounded-lg p-3">
            <label htmlFor="cardholder-name" className="block text-sm font-medium text-gray-700 mb-1">
              Cardholder name
            </label>
            <input
              id="cardholder-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name on card"
              autoComplete="cc-name"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#1f3d32] focus:border-transparent"
            />
          </div>

          <div className="border border-gray-300 rounded-lg p-3">
            <PaymentElement />
          </div>
        </div>
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-sm ${
          message.includes("successfully") || message.includes("saved") 
            ? "bg-green-50 text-green-800" 
            : message.includes("processing")
            ? "bg-blue-50 text-blue-800"
            : "bg-red-50 text-red-800"
        }`}>
          {message}
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading || !stripe || !elements || !email || !name.trim()}
        className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
          isLoading || !stripe || !elements || !email || !name.trim()
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-[#1f3d32] text-white hover:bg-[#1f3d32]/90"
        }`}
      >
        {isLoading ? "Saving..." : "Save Payment Method"}
      </button>
    </form>
  );
};

export default CheckoutForm;
